import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  customDisplayFormatService,
  type CustomDisplayFormat,
  type CustomDisplayFormatInput,
} from '../services/customDisplayFormat.service';
import { useAuthToken } from './useAuthToken';

const QUERY_KEY = ['customDisplayFormats'];

export function useCustomDisplayFormats(options?: { enabled?: boolean }) {
  const { enabled = true } = options || {};
  // Wait for auth — this hook fires from FormatLocaleProvider at the tree root
  // (mirrors useTenantConfig), which mounts before the JWT is exchanged.
  const token = useAuthToken();
  return useQuery<CustomDisplayFormat[]>({
    queryKey: QUERY_KEY,
    queryFn: () => customDisplayFormatService.list(),
    enabled: enabled && !!token,
    staleTime: 5 * 60 * 1000,
  });
}

export function useCreateCustomDisplayFormat() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: CustomDisplayFormatInput) => customDisplayFormatService.create(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: QUERY_KEY });
    },
  });
}

export function useUpdateCustomDisplayFormat() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: CustomDisplayFormatInput }) =>
      customDisplayFormatService.update(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: QUERY_KEY });
    },
  });
}

export function useDeleteCustomDisplayFormat() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => customDisplayFormatService.delete(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: QUERY_KEY });
    },
  });
}
